import FormattedPrice from '@components/FormattedPrice'
import { AddedProductVariantFragment } from '@graphql/fragments/__generated__/AddedProductVariant.generated'
import { ApolloCache } from '@apollo/client'
import Button, { ButtonProps } from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import React, { useContext, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { syncCartCache } from '../../../utils/cartCache'
import { googlePushProductAddToCart } from '../../../utils/dataLayer'

export interface PriceRange {
  currency: string
  discount: { min: number; max: number }
  undiscount: { min: number; max: number }
}

// What the card needs to show the "added to cart" panel, for every add-to-cart mutation of the panel.
export interface AddedToCartResult {
  variant: AddedProductVariantFragment
  quantity: number
  supplyLinesJson?: { quantity: number; price: number }[] | null
}

const scaleRange = ({ min, max }: { min: number; max: number }, factor: number) => ({
  min: min * factor,
  max: max * factor,
})

// Price of `quantity` items (kits), for the price shown next to the quantity stepper.
export const multiplyPriceRange = (range: PriceRange, quantity: number): PriceRange => ({
  currency: range.currency,
  discount: scaleRange(range.discount, quantity),
  undiscount: scaleRange(range.undiscount, quantity),
})

// A kit's range spans its cheapest part up to the full kit; the full kit is what gets added (as on the product page).
export const fullKitPriceRange = (range: PriceRange): PriceRange => ({
  ...range,
  discount: { min: range.discount.max, max: range.discount.max },
  undiscount: { min: range.undiscount.max, max: range.undiscount.max },
})

export const Price: React.FC<{ range: PriceRange; separator?: string }> = ({ range, separator = '·' }) => {
  const { currency, discount, undiscount } = range
  const isSale = undiscount.min !== discount.min || undiscount.max !== discount.max
  const format = (min: number, max: number) =>
    min === max ? (
      <FormattedPrice currency={currency} amount={min} />
    ) : (
      <>
        <FormattedPrice currency={currency} amount={min} /> {separator}{' '}
        <FormattedPrice currency={currency} amount={max} />
      </>
    )
  return (
    <span className='product-card-panel__price'>
      {isSale && <span className='product-card-panel__price-old'>{format(undiscount.min, undiscount.max)}</span>}
      <span className='data-hj-allow'>{format(discount.min, discount.max)}</span>
    </span>
  )
}

// Buttons of the panel. Styled through `sx`: MUI's styles are injected after the SCSS, so class rules of equal
// specificity would lose. The `product-card-panel__*` classes stay for the narrow-card overrides in the SCSS.

const baseButtonSx = {
  borderRadius: '8px',
  textTransform: 'none',
  letterSpacing: 'normal',
  lineHeight: 1.2,
  fontWeight: 500,
} as const

export const SUBMIT_HEIGHT = 'var(--product-card-submit-height, 44px)'

// Main action at the bottom of the panel ("Add to Cart", "Add PDF Pattern to Cart", ...).
export const SubmitButton: React.FC<ButtonProps & { loading?: boolean; icon?: React.ReactNode }> = ({
  loading,
  icon,
  className = '',
  sx,
  ...props
}) => (
  <Button
    variant='contained'
    color='secondary'
    fullWidth
    disableElevation
    startIcon={loading ? <CircularProgress size={20} color='inherit' /> : icon}
    className={`product-card-panel__submit ${className}`}
    sx={{
      ...baseButtonSx,
      borderRadius: '5px',
      // Bigger in ProductCardSheet (see the SCSS).
      minHeight: SUBMIT_HEIGHT,
      px: 1.5,
      fontSize: 15,
      '& .MuiButton-startIcon > svg': { fontSize: 22 },
      '&.Mui-disabled': { backgroundColor: '#b5b5b5', color: '#fff' },
      ...sx,
    }}
    {...props}
  />
)

// "KIT" / "PDF Pattern" toggle.
export const TypeButton: React.FC<{ selected: boolean; icon: React.ReactNode; label: string; onClick(): void }> = ({
  selected,
  icon,
  label,
  onClick,
}) => (
  <Button
    variant={selected ? 'contained' : 'outlined'}
    color='secondary'
    disableElevation
    role='radio'
    aria-checked={selected}
    startIcon={icon}
    onClick={onClick}
    className='product-card-panel__type'
    sx={{
      ...baseButtonSx,
      flex: '1 1 0',
      minWidth: 0,
      height: 40,
      px: 1,
      fontSize: 14,
      border: '1.5px solid',
      borderColor: 'secondary.main',
      '&:hover': { border: '1.5px solid', borderColor: 'secondary.main', ...(!selected && { bgcolor: '#eafaf9' }) },
      '& .MuiButton-startIcon': { mr: 0.75 },
      '& .MuiButton-startIcon > svg': { fontSize: 22 },
    }}
  >
    <span className='truncate'>{label}</span>
  </Button>
)

// Secondary text action ("Customize the KIT").
export const LinkButton: React.FC<ButtonProps & { loading?: boolean }> = ({ loading, sx, ...props }) => (
  <Button
    variant='text'
    color='secondary'
    size='small'
    startIcon={loading ? <CircularProgress size={14} color='inherit' /> : undefined}
    sx={{
      ...baseButtonSx,
      // Fits the 30px extra row (see ExtraRow).
      height: 30,
      minHeight: 0,
      py: 0,
      fontSize: 15,
      fontWeight: 600,
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      '&:hover': { textDecoration: 'underline', bgcolor: 'transparent', color: '#69c0b2' },
      ...sx,
    }}
    {...props}
  />
)

// Every form of the panel is the same three rows: this price row (or the quantity stepper with the total, which
// uses the same class), an ExtraRow and the SubmitButton. The rows have fixed heights (see the SCSS), so switching
// between KIT and PDF, or the skeleton being replaced by the form, doesn't move anything.
export const PriceRow: React.FC<{ muted?: boolean }> = ({ muted, children }) => {
  const { t } = useTranslation()
  return (
    <div className='product-card-panel__price-row'>
      <span className={muted ? 'product-card-panel__muted' : ''}>{t('productCard.onePiece', '1 piece')}</span>
      {children}
    </div>
  )
}

// Height of the extra row plus the panel's gap above it (`.product-card-panel__extra` and `gap` in the SCSS).
export const EXTRA_ROW_SPACE = 40

// An empty extra row collapses instead of leaving a gap above the button, and expands smoothly when something
// appears in it; the card is told, so it can resize its image in the same frame and keep its height.
export const ExtraRowContext = React.createContext<{
  // The panel's first render (opening): the row starts in its final state, with nothing to animate yet.
  initial: boolean
  // The panel's opening animation has finished. Until then a change waits: changing the card's image scale in the
  // middle of that animation would restart the image's transition out of step with the panel's.
  settled: boolean
  // The state of the previous row: a variant change mounts a new form, whose row starts from it.
  lastCollapsed: React.MutableRefObject<boolean>
  onCollapsedChange?(collapsed: boolean): void
}>({ initial: false, settled: true, lastCollapsed: { current: false } })

// The row between the price and the button: a hint, "N files in product", "Customize the KIT"...
export const ExtraRow: React.FC = ({ children }) => {
  const { initial, settled, lastCollapsed, onCollapsedChange } = useContext(ExtraRowContext)
  const ref = useRef<HTMLDivElement>(null)
  const collapsed = React.Children.toArray(children).length === 0
  // Animating on the panel's first render would overlap with the panel's own opening animation and move the card.
  const [shownCollapsed, setShownCollapsed] = useState(() => (initial ? collapsed : lastCollapsed.current))

  useLayoutEffect(() => {
    if (!initial && !settled) return
    if (shownCollapsed !== collapsed) {
      // Lay out the current state first, so the height transition runs.
      void ref.current?.offsetHeight
      setShownCollapsed(collapsed)
    }
    lastCollapsed.current = collapsed
    onCollapsedChange?.(collapsed)
  }, [collapsed, settled])

  return (
    <div
      ref={ref}
      className={`product-card-panel__extra ${shownCollapsed ? 'product-card-panel__extra--collapsed' : ''}`}
    >
      {children}
    </div>
  )
}

// Shared `update` of the panel's add-to-cart mutations: points the cache at the returned cart and marks the
// variant as added, so reopening the card shows "Already in the cart" without a refetch.
export const updateCacheAfterAdd = (
  cache: ApolloCache<unknown>,
  result?: { cart?: Parameters<typeof syncCartCache>[1]; variant?: { id: string } | null } | null
) => {
  syncCartCache(cache, result?.cart)
  if (result?.variant) {
    cache.modify({
      id: cache.identify({ __typename: 'ProductVariantType', id: result.variant.id }),
      fields: { isInCart: () => true },
    })
  }
}

export const pushAddToCartEvent = (
  result: AddedToCartResult,
  {
    name,
    categoryName,
    currency,
    unitPrice,
  }: { name: string; categoryName?: string; currency: string; unitPrice: number }
) => {
  // A kit is worth the sum of its parts (per kit), like on the product page.
  const price = result.supplyLinesJson?.length
    ? result.supplyLinesJson.reduce((sum, line) => sum + line.quantity * line.price, 0)
    : unitPrice
  googlePushProductAddToCart({
    currency,
    value: price * result.quantity,
    items: [
      {
        item_id: result.variant.sku,
        item_name: name,
        index: 0,
        item_category: categoryName,
        item_variant: result.variant.properties.map(prop => `${prop.attributeName}: ${prop.valueName}`).join(),
        price,
        quantity: result.quantity,
      },
    ],
  })
}
